import { request } from '@/renderer/utils/api';
import { getAttribution } from '@/renderer/utils/stat/attribution';

export type StripeOrderResponse = {
  calculated: {
    balanceUsed: number;
    plays: number;
    originalPrice: number;
    price: number;
  };
  ouuid: string;
  clientSecret?: string;
  doLocalPurchase?: boolean;
};

export type LocalOrderResponse = {
  calculated: {
    balanceUsed: number;
    plays: number;
    originalPrice: number;
    price: number;
  };
  orders: API.MembershipOrder[];
  ouuid: string;
};

export async function createStripeOrder(productId: string | number, index: number, affiliate = '') {
  return (
    await request<{ data: StripeOrderResponse }>(
      { input: `/api/v1/order`, method: 'POST' },
      {
        body: {
          productId: `${productId}.${index}`,
          method: 'stripe',
          affiliate,
          // Marketing attribution (utm_*/twclid/referrer/landing) captured on
          // the buyer's campaigned landing; stored on the order server-side.
          ...getAttribution(),
        },
      },
    )
  ).data;
}

/**
 * Capture the buyer's marketing attribution when they choose to pay with
 * cryptocurrency. The crypto CTA sends them to support chat where the order is
 * fulfilled by hand, so we stash the same attribution the card checkout would
 * have forwarded (utm, twclid, referrer, landing) plus the requested offer on
 * the buyer's user record. The admin's manual order form reads it back to
 * prefill source / campaign / landing, so a crypto order ends up attributed
 * like a card order. Best-effort: the caller navigates to chat regardless.
 */
export async function saveCryptoOrderIntent(opts: {
  productId?: string;
  productTitle?: string;
}) {
  return request(
    { input: `/api/v1/order/crypto-intent`, method: 'POST' },
    {
      body: {
        ...(opts.productId ? { productId: opts.productId } : {}),
        ...(opts.productTitle ? { productTitle: opts.productTitle } : {}),
        ...getAttribution(),
      },
    },
  );
}

export async function createLocalOrder(productId: string | number, index: number) {
  return (
    await request<{ data: LocalOrderResponse }>(
      { input: `/api/v1/order/local/balance`, method: 'POST' },
      {
        body: {
          productId: `${productId}.${index}`,
          method: 'balance',
          ...getAttribution(),
        },
      },
    )
  ).data;
}
